type BG = {
  img_url: string;
  width: number;
  height: number;
};

/**
 * BBox로 inner SVG(경로 포함 실제 도형 경계) 중앙 정렬
 * - viewBox/scale 무시
 * - 배경 <image> 깔고, inner svg의 children을 <g>로 옮겨서 중앙 배치
 * - 타이밍 이슈 방지를 위해 오프스크린 SVG에 붙여서 2번 RAF 대기 후 getBBox()
 */
export async function composeSvgOverBg(
  svg_str: string,
  bg: BG,
  composeSvg: (a: SVGElement) => void,
): Promise<string> {
  const NS = 'http://www.w3.org/2000/svg';
  const XLINK = 'http://www.w3.org/1999/xlink';

  // 1) inner SVG 파싱
  const parser = new DOMParser();
  const innerDoc = parser.parseFromString(svg_str, 'image/svg+xml');
  const innerSvg = innerDoc.documentElement as SVGSVGElement;
  const innerSvgWidth = Number(innerSvg.getAttribute('width'));
  const innerSvgHeight = Number(innerSvg.getAttribute('height'));

  // 2) output SVG 생성
  const outerDoc = document.implementation.createDocument(NS, 'svg', null);
  const outer = outerDoc.documentElement as unknown as SVGSVGElement;
  outer.setAttribute('xmlns', NS);
  outer.setAttribute('xmlns:xlink', XLINK);
  outer.setAttribute('width', String(bg.width));
  outer.setAttribute('height', String(bg.height));
  outer.setAttribute('viewBox', `0 0 ${bg.width} ${bg.height}`);
  // 배경
  const img = outerDoc.createElementNS(NS, 'image');
  img.setAttributeNS(XLINK, 'xlink:href', bg.img_url);
  img.setAttribute('href', bg.img_url);
  img.setAttribute('x', '0');
  img.setAttribute('y', '0');
  img.setAttribute('width', String(bg.width));
  img.setAttribute('height', String(bg.height));
  outer.appendChild(img);

  // inner → g 치환(속성은 표현/스타일 위주 보존)
  const g = outerDoc.createElementNS(NS, 'g');
  // const keep = new Set([
  //   'id',
  // ]);
  // for (const a of Array.from(innerSvg.attributes)) {
  //   const n = a.name;
  //   if (n === 'width' || n === 'height' || n === 'viewBox') continue;
  //   if (keep.has(n) || n.startsWith('data-')) g.setAttribute(n, a.value);
  // }

  composeSvg(innerSvg);
  while (innerSvg.firstChild) {
    g.appendChild(outerDoc.importNode(innerSvg.firstChild, true));
    innerSvg.removeChild(innerSvg.firstChild);
  }
  outer.appendChild(g);

  // 3) 오프스크린에 붙여서 레이아웃 보장 후 BBox 측정
  const probe = document.createElementNS(NS, 'svg');
  probe.setAttribute('width', String(bg.width));
  probe.setAttribute('height', String(bg.height));
  probe.setAttribute('viewBox', `0 0 ${bg.width} ${bg.height}`);
  // display:none 이면 BBox가 0이므로, 화면 밖에 배치 + visibility:hidden
  Object.assign(probe.style, {
    position: 'fixed',
    left: '-10000px',
    top: '-10000px',
    visibility: 'hidden',
    pointerEvents: 'none',
  } as CSSStyleDeclaration);

  // outer 복제본을 probe에 붙여 측정 (원본은 직렬화용으로 유지)
  const clonedOuter = probe.ownerDocument!.importNode(outer, true) as SVGSVGElement;
  probe.appendChild(clonedOuter);
  document.body.appendChild(probe);

  // 렌더 사이클 2회 보장 (폰트/필터 등 반영 안정화)
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

  // 측정
  const clonedG = clonedOuter.querySelector('g') as SVGGElement;
  let bb = clonedG.getBBox();

  // 일부 브라우저에서 첫 프레임에 0 나오는 경우 한 번 더 대기
  if (bb.width === 0 && bb.height === 0) {
    await new Promise((r) => requestAnimationFrame(r));
    bb = clonedG.getBBox();
  }

  // 중앙 이동 벡터
  const tx = (bg.width - innerSvgWidth) / 2 - bb.x;
  const ty = (bg.height - innerSvgHeight) / 2 - bb.y;
  // 원본 g에 적용
  const prev = g.getAttribute('transform');
  g.setAttribute('transform', `${prev ? prev + ' ' : ''}translate(${tx},${ty})`);

  // 정리
  document.body.removeChild(probe);

  // (옵션) 가장자리 안정화
  g.setAttribute('vector-effect', 'non-scaling-stroke');
  (g.style as any).paintOrder = 'stroke fill markers';

  // 4) 직렬화 후 반환

  return new XMLSerializer().serializeToString(outer);
}
